import React, { useState, useMemo } from 'react';
import { ArrowLeftIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';

interface FishSeedLog {
  id: string;
  pond_id: string;
  stocking_date: string;
  fish_species: string | string[];
  species_breakdown?: {[key: string]: {weight_kg: number, count: number}};
  total_weight_kg?: number;
  fish_count?: number;
  avg_weight_g?: number;
  source_supplier?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
  pond_name?: string;
}

const FishSeedLogs: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();

  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [selectedPond, setSelectedPond] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState('');

  const buildEndpoint = () => {
    if (!selectedLocation) return '';
    let endpoint = `/locations/${selectedLocation.id}/fish-seed-logs`;
    const params: string[] = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (selectedPond) params.push(`pond_id=${selectedPond}`);
    if (params.length > 0) endpoint += `?${params.join('&')}`;
    return endpoint;
  };

  const { data: pondsData } = useApi(`/locations/${selectedLocation?.id}/ponds`);
  const { data: speciesData } = useApi<any[]>(
    selectedLocation?.id ? `/master-data/fish_species?location_id=${encodeURIComponent(selectedLocation.id)}` : '',
    {
      dependencies: [selectedLocation?.id],
      skip: !selectedLocation
    }
  );
  const { data: logs, loading, error, refetch } = useApi<FishSeedLog[]>(buildEndpoint(), {
    dependencies: [selectedLocation?.id, startDate, endDate, selectedPond],
    skip: !selectedLocation
  });

  const enrichedLogs = useMemo(() => {
    const base = !logs || !pondsData ? [] as any[] : (logs as any[]).map((log: any) => ({
      ...log,
      pond_name: (pondsData as any[]).find((p: any) => p.id === log.pond_id)?.name || 'Unknown Pond'
    }));
    
    if (selectedSpecies) {
      return base.filter((l: any) => {
        if (Array.isArray(l.fish_species)) {
          return l.fish_species.includes(selectedSpecies);
        }
        return l.fish_species === selectedSpecies;
      });
    }
    return base;
  }, [logs, pondsData, selectedSpecies]);

  const clearFilters = () => {
    const d = getDefaultDates();
    setStartDate(d.startDate);
    setEndDate(d.endDate);
    setSelectedPond('');
    setSelectedSpecies('');
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600"><ArrowLeftIcon className="h-5 w-5" /></Link>
          <h1 className="text-lg font-bold text-gray-800">🐠 Fish Seed Logs</h1>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><FunnelIcon className="h-4 w-4 text-gray-500" /><span className="text-sm font-medium text-gray-700">Filters</span></div>
            <div className="text-xs text-cyan-600 bg-cyan-50 px-2 py-1 rounded">Last 7 days</div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pond</label>
              <select value={selectedPond} onChange={(e) => setSelectedPond(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs">
                <option value="">All Ponds</option>
                {(pondsData as any[] || []).map((pond: any) => (<option key={pond.id} value={pond.id}>{pond.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fish Species</label>
              <select value={selectedSpecies} onChange={(e) => setSelectedSpecies(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs">
                <option value="">All Species</option>
                {(speciesData as any[] || []).map((s: any) => (<option key={s.id} value={s.name}>{s.name}</option>))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={clearFilters} className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors">Reset to 7 Days</button>
            <button onClick={refetch} className="flex-1 bg-cyan-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-cyan-700 transition-colors">Refresh</button>
          </div>
        </div>
      </div>

      {error && (<div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4"><p className="text-red-800 text-sm">{error}</p></div>)}
      {loading && (<div className="p-8 text-center"><LoadingSpinner size="md" /><p className="text-gray-500 mt-2">Loading fish seed logs...</p></div>)}

      {!loading && (
        <div className="p-4 space-y-3">
          {(enrichedLogs as any[]).length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🐠</div>
              <p className="text-gray-500 mb-2">No fish seed logs found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters or add new entries.</p>
              {user?.role !== 'user' && (
                <Link to="/data-entry/fish-seed" className="inline-block mt-4 bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-cyan-700 transition-colors">Add Fish Seed</Link>
              )}
            </div>
          ) : (
            (enrichedLogs as any[]).map((log: any) => (
              <div key={log.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h3 className="font-semibold text-gray-800">{log.pond_name}</h3>
                    <p className="text-sm text-gray-500">{fmtDate(log.stocking_date)}</p>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    {log.total_weight_kg !== undefined && <div>Weight: <span className="font-semibold">{log.total_weight_kg}</span> kg</div>}
                    {log.fish_count !== undefined && <div>Count: <span className="font-semibold">{log.fish_count}</span></div>}
                    {log.avg_weight_g !== undefined && <div>Avg: <span className="font-semibold">{log.avg_weight_g}</span> g</div>}
                  </div>
                </div>
                {log.fish_species && (
                  <div className="text-xs text-gray-700">
                    <div className="font-medium mb-1">
                      Species: {Array.isArray(log.fish_species) ? log.fish_species.join(', ') : log.fish_species}
                    </div>
                    {log.species_breakdown && (
                      <div className="mt-1 space-y-1">
                        {Object.entries(log.species_breakdown).map(([species, data]: [string, any]) => (
                          <div key={species} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                            <span className="font-medium">{species}</span>: {data.count} fish, {data.weight_kg}kg
                            {data.weight_kg && data.count && (
                              <span className="text-gray-500 ml-1">
                                (avg: {Math.round((data.weight_kg * 1000) / data.count)}g)
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {log.remarks && <div className="text-xs text-gray-700">Remarks: {log.remarks}</div>}
              </div>
            ))
          )}
        </div>
      )}

      {user?.role !== 'user' && (
        <div className="fixed bottom-6 right-6">
          <Link to="/data-entry/fish-seed" className="bg-cyan-600 text-white p-4 rounded-full shadow-lg hover:bg-cyan-700 transition-colors flex items-center gap-2">
            <span className="text-xl">🐠</span>
            <span className="text-sm font-medium">Add Fish Seed</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default FishSeedLogs;


